import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { setMode } from "@/lib/api";
import { trackDemo } from "@/lib/demoTrack";
import DemoBanner from "@/components/DemoBanner";
import {
  Loader2, ArrowLeft, ShieldCheck, Camera, Sparkles, Stethoscope, Scale, Plane,
  Clock, MessageSquare, FileText, Check, X, User, Car, Info, FolderLock,
} from "lucide-react";

const DEMO_MESSAGES = {
  rn: "Demo Mode: In the live version, a licensed Crash Ready Triage RN would be notified after your accident, identity, driver information and insurance information pass the required verification steps.",
  marketplace: "Demo Mode: In the live version, your verified case would become available to participating attorneys after completing the required verification and RN review.",
  drone: "Demo Mode: In the live version, a drone deployment request may be sent to an available FAA-certified pilot only after RN approval and all deployment requirements are satisfied.",
  evidence: "Demo Mode: In the live version, your evidence would first enter temporary encrypted storage and undergo verification before moving into your permanent Evidence Locker.",
  ai: "Demo Mode: In the live version, Crash Ready would analyze your information and evidence to create a Trust Score and determine which features may be unlocked.",
};

const TABS = [
  { key: "overview", label: "Overview", icon: Info },
  { key: "evidence", label: "Evidence", icon: Camera },
  { key: "score", label: "Trust Score", icon: Sparkles },
  { key: "rn", label: "RN Triage", icon: Stethoscope },
  { key: "marketplace", label: "Marketplace", icon: Scale },
  { key: "timeline", label: "Timeline", icon: Clock },
  { key: "messages", label: "Messages", icon: MessageSquare },
];

const SEVERITY = { Mild: "bg-amber-50 text-amber-600", Moderate: "bg-orange-50 text-orange-600", Serious: "bg-red-50 text-red-600" };

const SampleTag = () => (
  <span className="inline-flex text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Sample Demo Data</span>
);

const Card = ({ children, testid }) => (
  <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={testid}>{children}</div>
);

const DemoActionBtn = ({ icon: Icon, label, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-navy text-white text-sm font-semibold hover:opacity-90 transition-all active:scale-95">
    <Icon className="w-4 h-4" /> {label}
  </button>
);

export default function DemoMode() {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [tab, setTab] = useState("overview");
  const [demoMsg, setDemoMsg] = useState(null);

  useEffect(() => {
    setMode("DEMO");
    trackDemo("demo_opened", { source: sessionStorage.getItem("cr_demo_source") || "direct" });
    api.get("/demo/scenarios").then(({ data }) => setScenarios(data.scenarios)).catch(() => setScenarios([]));
  }, []);

  const pick = (id) => {
    trackDemo("scenario_opened", { scenario_id: id });
    api.get(`/demo/scenarios/${id}`).then(({ data }) => { setScenario(data.scenario); setTab("overview"); });
  };

  const showMsg = (key) => {
    trackDemo("demo_action", { action: key, scenario_id: scenario?.id });
    setDemoMsg(key);
  };

  const s = scenario;

  return (
    <div className="min-h-screen bg-background" data-testid="demo-mode-page">
      <DemoBanner />

      {demoMsg && (
        <div className="fixed inset-0 z-[105] bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="demo-message-modal" onClick={() => setDemoMsg(null)}>
          <div className="bg-white rounded-3xl p-7 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="h-12 w-12 rounded-2xl bg-amber-50 flex items-center justify-center"><Info className="w-6 h-6 text-amber-600" /></div>
            <p className="mt-4 text-sm text-slate-700 leading-relaxed" data-testid="demo-message-text">{DEMO_MESSAGES[demoMsg]}</p>
            <button onClick={() => setDemoMsg(null)} data-testid="demo-message-close"
              className="mt-5 w-full bg-navy text-white font-semibold py-3 rounded-full hover:opacity-90 transition-all">Got it</button>
          </div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {!s ? (
          <div className="cr-fade-up">
            <button onClick={() => { setMode("LIVE"); navigate(-1); }} data-testid="demo-back-btn" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy"><ArrowLeft className="w-4 h-4" /> Back</button>
            <p className="mt-4 text-xs font-medium text-amber-600 uppercase tracking-widest">Demo Experience</p>
            <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-navy mt-1">Choose a demonstration scenario</h1>
            <p className="mt-2 text-slate-600 max-w-2xl">Explore how Crash Ready documents an accident, verifies evidence, triages injuries and connects verified cases with attorneys — using preloaded sample data only.</p>
            {scenarios === null ? (
              <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-cobalt animate-spin" /></div>
            ) : (
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
                {scenarios.map((sc) => (
                  <button key={sc.id} onClick={() => pick(sc.id)} data-testid={`demo-scenario-${sc.id}`}
                    className="group text-left bg-white rounded-3xl border border-slate-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-xl transition-all">
                    <div className="relative">
                      <img src={sc.photo} alt={sc.label} className="h-40 w-full object-cover" />
                      <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-navy px-2 py-1 rounded-full">Sample Demo Data</span>
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading font-bold text-navy">{sc.label}</h3>
                      <p className="text-sm text-slate-500 mt-1">{sc.tagline}</p>
                      <div className="mt-3 text-xs font-semibold text-cobalt">Trust Score {sc.score} · Explore →</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="cr-fade-up">
            <button onClick={() => setScenario(null)} data-testid="demo-change-scenario" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-navy"><ArrowLeft className="w-4 h-4" /> Change scenario</button>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-navy">{s.label}</h1>
              <SampleTag />
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">Simulated Case — no real Case ID</span>
            </div>

            <div className="mt-5 flex gap-2 overflow-x-auto pb-1">
              {TABS.map(({ key, label, icon: Icon }) => (
                <button key={key} onClick={() => setTab(key)} data-testid={`demo-tab-${key}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${tab === key ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>

            <div className="mt-6 space-y-5">
              {tab === "overview" && (
                <>
                  <Card testid="demo-overview">
                    <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><FileText className="w-5 h-5 text-cobalt" /> Accident storyline <SampleTag /></h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.storyline}</p>
                  </Card>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Card testid="demo-driver-info">
                      <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><User className="w-5 h-5 text-cobalt" /> Driver information <SampleTag /></h3>
                      <div className="mt-3 space-y-2 text-sm">
                        {[["You", s.driver], ["Other driver", s.other_driver]].map(([who, d]) => (
                          <div key={who} className="rounded-xl bg-slate-50 p-3">
                            <div className="text-xs font-semibold text-slate-400 uppercase">{who}</div>
                            <div className="text-navy font-medium mt-0.5">{d.name}</div>
                            <div className="text-slate-500 text-xs mt-0.5">License {d.license} · {d.vehicle} · Plate {d.plate}</div>
                          </div>
                        ))}
                      </div>
                    </Card>
                    <Card testid="demo-insurance-info">
                      <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><Car className="w-5 h-5 text-cobalt" /> Insurance information <SampleTag /></h3>
                      <div className="mt-3 space-y-2 text-sm">
                        <div className="rounded-xl bg-slate-50 p-3">
                          <div className="text-xs font-semibold text-slate-400 uppercase">Your policy</div>
                          <div className="text-navy font-medium mt-0.5">{s.insurance.carrier}</div>
                          <div className="text-slate-500 text-xs">{s.insurance.policy}</div>
                        </div>
                        <div className="rounded-xl bg-slate-50 p-3">
                          <div className="text-xs font-semibold text-slate-400 uppercase">Other driver's policy</div>
                          <div className="text-navy font-medium mt-0.5">{s.insurance.other_carrier}</div>
                          <div className="text-slate-500 text-xs">{s.insurance.other_policy}</div>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium"><ShieldCheck className="w-3.5 h-3.5" /> {s.insurance.status}</div>
                      </div>
                    </Card>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <DemoActionBtn icon={Plane} label="Request Drone Deployment" onClick={() => showMsg("drone")} testid="demo-btn-drone" />
                    <DemoActionBtn icon={Sparkles} label="Run AI Analysis" onClick={() => showMsg("ai")} testid="demo-btn-ai" />
                  </div>
                </>
              )}

              {tab === "evidence" && (
                <>
                  <Card testid="demo-evidence">
                    <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><FolderLock className="w-5 h-5 text-cobalt" /> Evidence Locker <SampleTag /></h3>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                      {s.photos.map((p, i) => (
                        <div key={i} className="rounded-2xl overflow-hidden border border-slate-100 relative">
                          <img src={p.url} alt={p.caption} className="h-52 w-full object-cover" />
                          <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-navy px-2 py-1 rounded-full">Sample Demo Data</span>
                          <div className="p-3 text-sm text-slate-600 bg-white">{p.caption}</div>
                        </div>
                      ))}
                    </div>
                  </Card>
                  <DemoActionBtn icon={Camera} label="Submit New Evidence" onClick={() => showMsg("evidence")} testid="demo-btn-evidence" />
                </>
              )}

              {tab === "score" && (
                <>
                  <Card testid="demo-trust-score">
                    <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><Sparkles className="w-5 h-5 text-cobalt" /> Crash Ready Trust Score <SampleTag /></h3>
                    <div className="mt-4 flex items-center gap-6 flex-wrap">
                      <div className="relative h-28 w-28">
                        <svg viewBox="0 0 100 100" className="h-28 w-28 -rotate-90">
                          <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="10" />
                          <circle cx="50" cy="50" r="42" fill="none" stroke="#16a34a" strokeWidth="10" strokeLinecap="round"
                            strokeDasharray={`${(s.trust_score.score / 100) * 264} 264`} />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="font-heading text-3xl font-extrabold text-navy">{s.trust_score.score}</span>
                          <span className="text-xs font-bold text-emerald-600">Grade {s.trust_score.grade}</span>
                        </div>
                      </div>
                      <p className="flex-1 min-w-[240px] text-sm text-slate-600">{s.trust_score.summary}</p>
                    </div>
                  </Card>
                  <Card testid="demo-verification">
                    <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-cobalt" /> AI verification steps <SampleTag /></h3>
                    <div className="mt-3 space-y-2">
                      {s.verification.map((v) => (
                        <div key={v.step} className="flex items-center justify-between text-sm rounded-xl bg-slate-50 px-4 py-2.5">
                          <span className="text-slate-600">{v.step}</span>
                          <span className={`flex items-center gap-1 text-xs font-semibold ${v.status === "passed" ? "text-emerald-600" : "text-amber-600"}`}>
                            {v.status === "passed" ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />} {v.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>
                  <DemoActionBtn icon={Sparkles} label="Run AI Analysis" onClick={() => showMsg("ai")} testid="demo-btn-ai-2" />
                </>
              )}

              {tab === "rn" && (
                <>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Card testid="demo-rn-result">
                      <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><Stethoscope className="w-5 h-5 text-cobalt" /> RN triage result <SampleTag /></h3>
                      <div className="mt-3 space-y-2 text-sm">
                        <div className="flex items-center gap-2"><span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600">{s.rn_result.status}</span><span className="text-slate-500">{s.rn_result.nurse}</span></div>
                        <div className="rounded-xl bg-slate-50 p-3 text-navy font-medium">{s.rn_result.disposition}</div>
                        <p className="text-slate-600">{s.rn_result.recommendation}</p>
                      </div>
                      <div className="mt-4 space-y-2">
                        {s.injuries.map((inj) => (
                          <div key={inj.area} className="flex items-start justify-between gap-3 text-sm rounded-xl border border-slate-100 px-3 py-2.5">
                            <div><div className="font-medium text-navy">{inj.area}</div><div className="text-xs text-slate-500">{inj.note}</div></div>
                            <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${SEVERITY[inj.severity]}`}>{inj.severity}</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                    <Card testid="demo-body-map">
                      <h3 className="font-heading font-semibold text-navy">Injury body map <SampleTag /></h3>
                      <div className="mt-3 relative mx-auto w-44 h-80">
                        <svg viewBox="0 0 100 180" className="w-full h-full">
                          <circle cx="50" cy="14" r="10" fill="#cbd5e1" />
                          <rect x="36" y="26" width="28" height="50" rx="10" fill="#cbd5e1" />
                          <rect x="24" y="30" width="10" height="42" rx="5" fill="#cbd5e1" />
                          <rect x="66" y="30" width="10" height="42" rx="5" fill="#cbd5e1" />
                          <rect x="38" y="78" width="11" height="60" rx="5" fill="#cbd5e1" />
                          <rect x="52" y="78" width="11" height="60" rx="5" fill="#cbd5e1" />
                        </svg>
                        {s.body_map.map((pt) => (
                          <div key={pt.label} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: `${pt.x}%`, top: `${pt.y}%` }}>
                            <span className="h-4 w-4 rounded-full bg-red-500 border-2 border-white shadow-md animate-pulse" />
                            <span className="mt-0.5 text-[10px] font-semibold text-red-600 bg-white/90 px-1.5 rounded-full whitespace-nowrap">{pt.label}</span>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>
                  <DemoActionBtn icon={Stethoscope} label="Request Triage RN" onClick={() => showMsg("rn")} testid="demo-btn-rn" />
                </>
              )}

              {tab === "marketplace" && (
                <>
                  <Card testid="demo-marketplace">
                    <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><Scale className="w-5 h-5 text-cobalt" /> Attorney marketplace preview <SampleTag /></h3>
                    <div className="mt-4 rounded-2xl border border-slate-100 p-5 bg-slate-50">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="font-semibold text-navy">{s.marketplace_preview.headline}</div>
                        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-600">Score {s.marketplace_preview.score}</span>
                      </div>
                      <div className="mt-2 flex gap-2 flex-wrap text-xs">
                        {[["State", s.marketplace_preview.state], ["Injury", s.marketplace_preview.injury_level], ["Docs", s.marketplace_preview.docs]].map(([k, v]) => (
                          <span key={k} className="bg-white border border-slate-200 text-slate-600 px-2.5 py-1 rounded-full">{k}: <b className="text-navy">{v}</b></span>
                        ))}
                      </div>
                      <p className="mt-3 text-xs text-slate-500">This is how a verified case would appear to participating attorneys. In Demo Mode, nothing is listed.</p>
                    </div>
                  </Card>
                  <DemoActionBtn icon={Scale} label="List My Case in the Marketplace" onClick={() => showMsg("marketplace")} testid="demo-btn-marketplace" />
                </>
              )}

              {tab === "timeline" && (
                <Card testid="demo-timeline">
                  <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><Clock className="w-5 h-5 text-cobalt" /> Case timeline <SampleTag /></h3>
                  <div className="mt-4 space-y-0">
                    {s.timeline.map((t, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <span className="h-3 w-3 rounded-full bg-cobalt mt-1" />
                          {i < s.timeline.length - 1 && <span className="w-px flex-1 bg-slate-200" />}
                        </div>
                        <div className="pb-5">
                          <div className="text-xs text-slate-400">{t.time}</div>
                          <div className="text-sm text-navy font-medium">{t.title}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {tab === "messages" && (
                <Card testid="demo-messages">
                  <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><MessageSquare className="w-5 h-5 text-cobalt" /> Secure messaging <SampleTag /></h3>
                  <div className="mt-4 space-y-3">
                    {s.messages.map((msg, i) => (
                      <div key={i} className={`max-w-lg rounded-2xl p-4 text-sm ${msg.role === "attorney" ? "bg-cobalt-light" : "bg-slate-50"}`}>
                        <div className="text-xs font-semibold text-slate-500">{msg.from} · {msg.time}</div>
                        <p className="mt-1 text-navy">{msg.text}</p>
                      </div>
                    ))}
                  </div>
                  <p className="mt-4 text-xs text-slate-400">Sample conversation. In Demo Mode no messages are sent or received.</p>
                </Card>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
